import { type Context, createContext, useCallback, useContext, useMemo } from 'react';

import {
	type Agent,
	type AgentKnowledgeSource,
	type PostAgentPayload,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type AgentFormFields } from '../../common/types.ts';
import {
	type KnowledgeFilterService,
	KnowledgeFilterServiceImpl,
} from '../../services/knowledge-filter/index.ts';
import { useSiteContext } from '../site-id/index.tsx';

/** Do not import for non Atlassian studio */
export const KnowledgeFilterServiceContext: Context<KnowledgeFilterService | null> =
	createContext<KnowledgeFilterService | null>(null);

/** Do not import for non Atlassian studio */
export const useKnowledgeFilterService = (): KnowledgeFilterService => {
	const { siteId } = useSiteContext();
	const serviceContext = useContext(KnowledgeFilterServiceContext);

	const knowledgeFilterService = useMemo(() => {
		if (serviceContext) {
			return serviceContext;
		}

		return new KnowledgeFilterServiceImpl(siteId);
	}, [serviceContext, siteId]);

	return knowledgeFilterService;
};

type KnowledgeSource = Pick<PostAgentPayload, 'knowledge_sources'>['knowledge_sources'];

type KnowledgeSourceFieldsFromScaffold = Pick<
	AgentFormFields,
	| 'knowledgeScope'
	| 'confluenceAncestorPageFilters'
	| 'confluenceFilterScope'
	| 'confluenceSpaceFilters'
	| 'jiraProjectFilters'
	| 'knowledgeEnabledFields'
>;

export const useKnowledgeFilterFormField = (): {
	getKnowledgeSourcesFromFormFields: (fields: AgentFormFields) => KnowledgeSource;
	getKnowledgeSourceFieldsFromScaffold: (agent: Agent) => Promise<Partial<AgentFormFields>>;
} => {
	const { getEntitiesByAris } = useKnowledgeFilterService();

	const getKnowledgeSourcesFromFormFields = useCallback(
		(fields: AgentFormFields): KnowledgeSource => {
			const sources: AgentKnowledgeSource[] = [];
			for (const fieldSource in fields.knowledgeEnabledFields) {
				if (fields.knowledgeEnabledFields[fieldSource]) {
					let configuration;
					if (fieldSource === 'confluence') {
						configuration = {
							space_filter:
								fields.confluenceSpaceFilters && fields.confluenceFilterScope?.value === 'spaces'
									? fields.confluenceSpaceFilters.map((filter) => filter.ari)
									: [],
							parent_filter:
								fields.confluenceAncestorPageFilters &&
								fields.confluenceFilterScope?.value === 'pages'
									? fields.confluenceAncestorPageFilters?.map((filter) => filter.ari)
									: [],
						};
					} else if (fieldSource === 'jira') {
						configuration = {
							project_filter: fields.jiraProjectFilters?.map((filter) => filter.ari) ?? [],
						};
					}

					sources.push({
						enabled: true,
						source: fieldSource,
						configuration,
					} as AgentKnowledgeSource);
				}
			}

			return {
				enabled: fields.knowledgeScope,
				sources,
			};
		},
		[],
	);

	const getKnowledgeSourceFieldsFromScaffold = useCallback(
		async (agent: Agent): Promise<Partial<AgentFormFields>> => {
			const { knowledge_sources } = agent;
			if (!knowledge_sources?.enabled) {
				return {
					knowledgeScope: false,
				};
			}

			let config: KnowledgeSourceFieldsFromScaffold = {
				knowledgeScope: true,
				knowledgeEnabledFields: {},
			};

			const filterPromises: Promise<void>[] = [];

			for (const { source, configuration: knowledgeConfig, enabled } of knowledge_sources.sources) {
				switch (source) {
					case 'confluence':
						config.knowledgeEnabledFields.confluence = enabled;

						if (knowledgeConfig?.parent_filter.length) {
							filterPromises.push(
								getEntitiesByAris({
									aris: knowledgeConfig.parent_filter,
								}).then((filters) => {
									config.confluenceAncestorPageFilters = filters.map((page) => ({
										ari: page.ari,
										label: page.displayName,
										value: page.ari,
										url: page.url,
									}));
									config.confluenceFilterScope = {
										label: '',
										value: 'pages',
									};
								}),
							);
						}

						if (knowledgeConfig?.space_filter.length) {
							filterPromises.push(
								getEntitiesByAris({
									aris: knowledgeConfig.space_filter,
								}).then((filters) => {
									config.confluenceSpaceFilters = filters.map((page) => ({
										ari: page.ari,
										label: page.displayName,
										value: page.ari,
										iconUrl: page.iconUrl,
										url: page.url,
									}));
									config.confluenceFilterScope = {
										label: '',
										value: 'spaces',
									};
								}),
							);
						}

						break;

					case 'jira':
						config.knowledgeEnabledFields.jira = enabled;
						if (knowledgeConfig) {
							filterPromises.push(
								getEntitiesByAris({
									aris: knowledgeConfig.project_filter,
								}).then((filters) => {
									config.jiraProjectFilters = filters.map((page) => ({
										ari: page.ari,
										label: page.displayName,
										value: page.ari,
										iconUrl: page.iconUrl,
										url: page.url,
									}));
								}),
							);
						}

						break;
					default:
						config.knowledgeEnabledFields[source] = enabled;

						break;
				}
			}

			await Promise.all(filterPromises);

			return config;
		},
		[getEntitiesByAris],
	);

	return {
		getKnowledgeSourcesFromFormFields,
		getKnowledgeSourceFieldsFromScaffold,
	};
};
